<script setup lang="ts">
import { ref } from "vue";
import EquipmentIndex from "@/components/equipment/EquipmentIndex.vue";
import EquipmentTable from "@/components/equipment/EquipmentTable.vue";
import AssignToAnotherModel from "@/components/equipment/AssignToAnotherModel.vue";
import { Button } from "@/shadcn/components/ui/button/index.js";
import { ForwardIcon, Trash2Icon } from "@lucide/vue";
import { useBulkActionApi } from "@/composables/useBulkActionApi";
import { toast } from "vue-sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/shadcn/components/ui/alert-dialog/index.js";

const props = defineProps({
  baseFilters: {
    type: Object,
    default: () => ({})
  },
  availableFilters: {
    type: Array,
    default: () => ([])
  },
  pushHistory: {
    type: Boolean,
    default: false
  },
  withExport: {
    type: Boolean,
    default: false
  },
  bulkActionUrl: {
    type: String,
    default: undefined
  },
  productMetafields: {
    type: Array,
    default: () => []
  },
  equipmentMetafields: {
    type: Array,
    default: () => []
  },
  include: {
    type: Array,
    default: () => []
  },
  addUrl: {
    type: String,
    default: null
  },
  enableContainer: {
    type: Boolean,
    default: false
  },
  columns: {
    type: Array,
    default: () => [],
  },
  productOptions: {
    type: Array,
    default: () => []
  },
});

const selectedEquipments = ref([]);
const showAssignDialog = ref(false);
const showDeleteConfirmation = ref(false);

const { loading: waitingBulkResult, execute: executeBulkAction } = useBulkActionApi(props.bulkActionUrl || '');

const onSelected = (equipmentId) => {
  if (!selectedEquipments.value.includes(equipmentId)) {
    selectedEquipments.value.push(equipmentId);
  }
};

const onDeselected = (equipmentId) => {
  const index = selectedEquipments.value.indexOf(equipmentId);
  if (index > -1) {
    selectedEquipments.value.splice(index, 1);
  }
};

const onClickBulkAction = (label) => {
  if (selectedEquipments.value.length === 0) {
    return;
  }
  showAssignDialog.value = true;
};

const onClickDeleteEquipments = () => {
  if (selectedEquipments.value.length === 0) {
    return;
  }
  showDeleteConfirmation.value = true;
};

const onConfirmDeleteEquipments = async (getEquipments) => {
  if (waitingBulkResult.value) {
    return;
  }
  const response = await executeBulkAction('delete-equipment', selectedEquipments.value);
  if (response.errors) {
    response.errors.forEach(error => {
      toast.error(error)
    })
  } else {
    toast.success('Equipment(s) deleted successfully');
  }
  showDeleteConfirmation.value = false;
  selectedEquipments.value = [];
  if (getEquipments) {
    await getEquipments();
  }
};

const onAssignSuccess = async (searchableModelRef, getEquipments) => {
  showAssignDialog.value = false;
  selectedEquipments.value = [];
  if (getEquipments) {
    await getEquipments();
  }
};
</script>
<template>
  <div>
    <EquipmentIndex
      :base-filters="props.baseFilters"
      :available-filters="availableFilters"
      :push-history="pushHistory"
      :with-export="withExport"
      :with-equipment-actions="true"
      :product-metafields="productMetafields"
      :equipment-metafields="equipmentMetafields"
      :include="include"
      :add-url="addUrl"
      :enable-container="enableContainer"
    >
      <template #header-title v-if="$slots['header-title']">
        <slot name="header-title"></slot>
      </template>

      <template #header-actions v-if="$slots['header-actions']">
        <slot name="header-actions"></slot>
      </template>

      <template #default="{ equipments, searchableModelRef, selectable, getEquipments, productMetafields, equipmentMetafields }">
        <template v-if="selectedEquipments.length > 0 && bulkActionUrl">
          <div class="d-print-none tw:flex tw:items-center tw:gap-2 tw:mb-4 mx-auto px-3 tw:max-w-400">
            <span class="">
              {{ $t('Bulk actions:') }}
            </span>
            <Button variant="outline"
                @click="onClickBulkAction($t('Assign to another model'))">
              <ForwardIcon class="tw:w-3 tw:h-3"/>
              {{ $t('Assign to another model') }}
            </Button>
            <Button variant="ghost-destructive"
                @click="onClickDeleteEquipments">
              <Trash2Icon class="tw:w-3 tw:h-3"/>
              {{ $t('Delete') }}
            </Button>
            <span class="tw:ml-auto tw:text-zinc-500">
              {{ $t("Item(s) selected", {count: selectedEquipments.length}, selectedEquipments.length) }}
            </span>
          </div>
        </template>
        
        <EquipmentTable
          :equipments="equipments"
          :equipments-selected="selectedEquipments"
          :selectable="selectable"
          :product-metafields="productMetafields"
          :equipment-metafields="equipmentMetafields"
          :columns="columns"
          @selected="onSelected"
          @deselected="onDeselected"
        />

        <AssignToAnotherModel
          v-if="showAssignDialog && bulkActionUrl"
          :open="showAssignDialog"
          @update:open="showAssignDialog = $event"
          :options="productOptions"
          :action="bulkActionUrl"
          :equipment-ids="selectedEquipments"
          @success="onAssignSuccess(searchableModelRef, getEquipments)"
        />

        <AlertDialog :open="showDeleteConfirmation" @update:open="showDeleteConfirmation = $event">
          <AlertDialogContent class="tw:!max-w-sm tw:gap-6">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {{ $t('Delete item(s)', selectedEquipments.length) }}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {{ $t('Are you sure you want to delete the selected item(s)?', selectedEquipments.length) }}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel asChild>
                <Button variant="secondary" @click="showDeleteConfirmation = false" :disabled="waitingBulkResult">{{ $t('Cancel') }}</Button>
              </AlertDialogCancel>
              <AlertDialogAction asChild  variant="destructive">
                <Button @click="onConfirmDeleteEquipments(getEquipments)"
                        :loading="waitingBulkResult">{{ $t('Confirm') }}</Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </template>
    </EquipmentIndex>
  </div>
</template>